import { Alert, Anchor, Button, Group, PasswordInput, PinInput, Stack, Text, TextInput } from '@mantine/core';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertCircle } from 'react-icons/fi';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { landingPath, useAuth } from '@web/lib/auth';
import { errorMessage } from '@web/lib/errors';
import { useLogin, useVerifyMfa } from './api';
import { AuthLayout } from './AuthLayout';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { status, me, signIn } = useAuth();
  const login = useLogin();
  const verify = useVerifyMfa();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');

  const from = (location.state as { from?: string } | null)?.from;

  if (status === 'authenticated' && me) {
    return <Navigate to={from && !from.startsWith('/login') ? from : landingPath(me)} replace />;
  }

  const finish = async (session: Parameters<typeof signIn>[0]) => {
    await signIn(session);
    const current = useAuth.getState().me;
    if (!current) return;
    const forced = current.user.mustChangePassword || current.user.mfaSetupRequired;
    navigate(!forced && from && !from.startsWith('/login') ? from : landingPath(current), { replace: true });
  };

  const submitPassword = (e: FormEvent) => {
    e.preventDefault();
    login.mutate(
      { email: email.trim(), password },
      {
        onSuccess: (res) => {
          if ('mfaRequired' in res) {
            setMfaToken(res.mfaToken);
            setCode('');
          } else {
            void finish(res);
          }
        },
      },
    );
  };

  const submitCode = (e?: FormEvent, value?: string) => {
    e?.preventDefault();
    if (!mfaToken) return;
    const body = useRecovery ? { mfaToken, recoveryCode: recoveryCode.trim() } : { mfaToken, code: value ?? code };
    verify.mutate(body, { onSuccess: (res) => void finish(res), onError: () => setCode('') });
  };

  if (mfaToken) {
    return (
      <AuthLayout title={t('auth.mfa.title')} subtitle={useRecovery ? t('auth.mfa.recoverySubtitle') : t('auth.mfa.subtitle')}>
        <form onSubmit={submitCode} noValidate>
          <Stack gap="md">
            {verify.isError && (
              <Alert color="red" icon={<FiAlertCircle />} title={t('auth.mfa.failed')}>
                {errorMessage(verify.error)}
              </Alert>
            )}
            {useRecovery ? (
              <TextInput
                label={t('auth.mfa.recoveryLabel')}
                placeholder="xxxx-xxxx-xxxx"
                value={recoveryCode}
                onChange={(e) => setRecoveryCode(e.currentTarget.value)}
                autoFocus
                autoComplete="off"
                required
              />
            ) : (
              <Group justify="center">
                <PinInput
                  length={6}
                  type="number"
                  oneTimeCode
                  autoFocus
                  value={code}
                  onChange={setCode}
                  onComplete={(v) => submitCode(undefined, v)}
                  aria-label={t('auth.mfa.codeLabel')}
                />
              </Group>
            )}
            <Button type="submit" fullWidth loading={verify.isPending} disabled={useRecovery ? recoveryCode.trim().length < 8 : code.length !== 6}>
              {t('auth.mfa.verify')}
            </Button>
            <Group justify="space-between">
              <Anchor component="button" type="button" size="sm" onClick={() => setUseRecovery((v) => !v)}>
                {useRecovery ? t('auth.mfa.useApp') : t('auth.mfa.useRecovery')}
              </Anchor>
              <Anchor
                component="button"
                type="button"
                size="sm"
                onClick={() => {
                  setMfaToken(null);
                  setPassword('');
                  verify.reset();
                }}
              >
                {t('auth.backToLogin')}
              </Anchor>
            </Group>
          </Stack>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('auth.login.title')} subtitle={t('auth.login.subtitle')}>
      <form onSubmit={submitPassword} noValidate>
        <Stack gap="md">
          {login.isError && (
            <Alert color="red" icon={<FiAlertCircle />} title={t('auth.login.failed')}>
              {errorMessage(login.error)}
            </Alert>
          )}
          <TextInput
            label={t('auth.email')}
            type="email"
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoFocus
            required
          />
          <PasswordInput
            label={t('auth.password.label')}
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="current-password"
            required
          />
          <Button type="submit" fullWidth loading={login.isPending} disabled={!email.trim() || !password}>
            {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
          </Button>
          <Group justify="flex-end">
            <Anchor component={Link} to="/forgot-password" size="sm">
              {t('auth.login.forgot')}
            </Anchor>
          </Group>
        </Stack>
      </form>
      <Text size="xs" c="dimmed" ta="center" mt="lg">
        {t('auth.login.noAccount')}
      </Text>
    </AuthLayout>
  );
}
